import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  OnInit,
} from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable } from 'rxjs';
import { ParentFacilityDto } from '@ups/xplat/api/dto';
import {
  SearchFacilityService,
  SearchFacilityVPService,
  SearchProspectFacilityService,
} from '@ups/xplat/api/services';
import { dynamicTypeaheadFacilityFilter } from '@ups/xplat/dynamic-render';
import { DynamicTypeaheadWebBaseComponent } from '../base/dynamic-typeahead.web-base-component';
import {
  FACILITY_SELECTION_KEY,
  isScopedFacilitySearch,
  searchFacilitiesWithProspects,
} from './facility-prospect-search';

const SEARCH_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DynamicTypeaheadFacilityComponent),
  multi: true,
};

@Component({
  selector: 'ups-dynamic-typeahead-facility',
  templateUrl: 'dynamic-typeahead-facility.component.html',
  providers: [SEARCH_CONTROL_VALUE_ACCESSOR],
  // WI 35584: template state is marked at each async write (loading, search results, target
  // updates) in the shared dynamic-render bases.
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DynamicTypeaheadFacilityComponent
  extends DynamicTypeaheadWebBaseComponent
  implements OnInit
{
  private readonly searchFacilityService = inject(SearchFacilityService);
  private readonly searchFacilityVPService = inject(SearchFacilityVPService);
  private readonly searchProspectFacilityService = inject(
    SearchProspectFacilityService
  );

  /**
   * The Kendo valueField. DisplayName, as always, unless the picker lists prospects (US 36008),
   * where a unique per-source key keeps a prospect selectable beside a same-named facility.
   */
  valueField = 'DisplayName';

  constructor() {
    super();
    this.queryFilter = dynamicTypeaheadFacilityFilter;
  }

  ngOnInit() {
    super.ngOnInit();
    this.searchQuery = this.config?.options?.pointToSparta
      ? this.searchFacilityService.search.bind(this.searchFacilityService)
      : this.searchFacilityVPService.search.bind(this.searchFacilityVPService);
    if (this.config?.options?.includeProspectFacilities) {
      this.listProspectFacilities(this.searchQuery);
    }
  }

  /** US 36008 (opt-in): Viewpoint facilities first, then Sparta prospect sites, each labelled. */
  private listProspectFacilities(
    facilitySearch: DynamicTypeaheadWebBaseComponent['searchQuery']
  ) {
    this.valueField = FACILITY_SELECTION_KEY;
    const scopeParamName =
      this.config?.options?.scopeQueryWhenFormControlIsSet?.queryParamName;
    this.searchQuery = (query: unknown, params: unknown) =>
      searchFacilitiesWithProspects({
        customers$: facilitySearch(query, params) as Observable<
          ParentFacilityDto[]
        >,
        prospects: () =>
          this.searchProspectFacilityService.search(query as string),
        scoped: isScopedFacilitySearch(params as HttpParams, scopeParamName),
      });
  }

  toggleFavoriteFacility(facility: ParentFacilityDto, e: PointerEvent) {
    // US 36008: a prospect row has no ParentFacilityID, so it cannot be a favorite (PR 5971 review).
    if (facility.ParentFacilityID == null) {
      e.stopPropagation();
      return;
    }
    facility.IsUserFavorite = !facility.IsUserFavorite;
    if (this.config?.options?.pointToSparta) {
      this.searchFacilityService.addOrRemoveFavoriteFacility(
        facility.ParentFacilityID
      );
    } else {
      this.searchFacilityVPService.addOrRemoveFavoriteFacility(
        facility.ParentFacilityID
      );
    }
    e.stopPropagation();
  }
}
